import type { ChartRow } from '@deepdive-ui';
import {
  chartPaletteSize,
  chartSeriesTokens,
  type ChartSeries,
  type ChartSeriesToken,
} from '@deepdive-ui';
import { autoBucket, bucketLabel, bucketStart, bucketsIn, type Bucket } from './buckets';
import { dimensionIndex, dimensionKeys, dimensionValues, labelOf, type DimensionKey } from './dimensions';
import {
  AGE_BANDS,
  COMMUNITY_VALUES,
  DIVISION_VALUES,
  GENDER_VALUES,
  ORIGIN_VALUES,
  OUTLET_VALUES,
  VOICE_VALUES,
  type MentionRow,
} from './generator';
import { metrics, type MetricId } from './metrics';

/**
 * Rows in, a chart out.
 *
 * Three properties fall out of doing it here rather than in a view:
 *
 *  1. **`series` is a *result*, not an input.** The keys the chart reads and the
 *     keys the rows carry are written by the same loop, so they cannot drift.
 *  2. **KPIs and charts reconcile by construction.** `total` reduces the very
 *     same array the buckets were built from — not a parallel query that could
 *     answer a slightly different question.
 *  3. **Colours are stable.** Each series' colour is indexed off the dimension's
 *     position in the *chip fixtures*, never off its position in this result.
 */

/**
 * Every aggregate and every pivot puts its x value on this key.
 *
 * **It begins with underscores so it cannot collide with a dimension value, and
 * that is a bug fix rather than a precaution.** A `ChartRow` is one flat object
 * holding the category on this key and each series' figure on the series' own
 * key — which are dimension slugs. The `Channels` chip offers `X`, whose slug is
 * `x`, so while this key was `'x'` any chart split by channel wrote that
 * channel's figure straight over its own category label.
 *
 * It was live on `main` in two Brand Health cards before the Community module
 * hit it a third time. Measured in the running app: `Engagement volume by type`
 * labelled its four columns `207 / 633 / 820 / 2574` instead of
 * `Shares / Comments / Reactions / Likes`, and `Relative engagement growth by
 * channel` labelled its date axis `100 / 30 / 0 / 0 / 0 / 0 / 0 / 93 / 0 / 3`.
 * Both read as a formatting quirk and neither throws, which is what let it sit
 * there — the row is still a valid row, it is just carrying the wrong string.
 *
 * Slugs come out of `label.toLowerCase().replace(/[^a-z0-9]+/g, '-')`, so they
 * can only contain `a-z`, `0-9` and `-`. A leading underscore is therefore
 * unreachable by construction rather than merely unlikely, and
 * `assertNoXKeyCollision` holds the line if either end of that ever changes.
 */
export const X_KEY = '__x';

/**
 * Fails at module load if any category value could shadow `X_KEY`.
 *
 * Cheap insurance against the bug above coming back by a different route — a
 * renamed chip option, a new roster, or someone shortening `X_KEY`. Runs over
 * the chips' own option lists and every roster in `generator.ts`, which together
 * are every string that can ever become a series key.
 */
export function assertNoXKeyCollision(): void {
  const rosters: readonly (readonly string[])[] = [
    ...dimensionKeys.map((key) => dimensionValues[key]),
    ORIGIN_VALUES,
    OUTLET_VALUES,
    COMMUNITY_VALUES,
    VOICE_VALUES,
    DIVISION_VALUES,
    AGE_BANDS,
    GENDER_VALUES,
  ];

  for (const values of rosters) {
    for (const value of values) {
      if (value === X_KEY) {
        throw new Error(
          `aggregate: the value "${value}" is also X_KEY, so a chart split by it would write ` +
            `that series' figure over its own category label. Rename one of the two.`,
        );
      }
    }
  }
}

export type AggregateSpec = {
  metric: MetricId;
  /** Inclusive day bounds, as local-midnight timestamps. */
  from: number;
  to: number;
  /** Omit to size the bucket from the range. */
  bucket?: Bucket;
  /** Split into one series per value of this dimension. Omit for a single series. */
  splitBy?: DimensionKey;
};

export type AggregateResult = {
  rows: ChartRow[];
  xKey: typeof X_KEY;
  series: ChartSeries[];
  /** The metric over **all** the rows — the figure a KPI shows. */
  total: number;
  /** The bucket size actually used, for the axis label and the tooltip. */
  bucket: Bucket;
  /**
   * The corpus rows behind one point on the chart — every row in that bucket,
   * across every series.
   *
   * What a readout's `View details` opens (2026-09-24). The grouping already
   * exists to compute the point, so this hands back the array it was reduced
   * from rather than re-filtering the corpus: a drill-down that re-derived its
   * own subset could disagree with the figure the reader clicked, which is the
   * one thing it must never do.
   *
   * **Whole bucket, not one series.** Clicking 08 Sep on an eleven-series plot
   * asks about 08 Sep — see `ChartBaseProps.onViewDetails`.
   *
   * Out of range, it is empty rather than an error: a stale index outliving a
   * data change is a real state, and an empty modal is the honest answer to it.
   */
  rowsAt: (index: number) => MentionRow[];
};

/**
 * **Sentiment is coloured by meaning, not by position.**
 *
 * These are the three `component/progress` values — `chart/4`, `chart/5`,
 * `chart/6` — which is the system's existing answer for "good / middling /
 * bad". Every other dimension takes its colour from where it sits in the chip
 * fixtures, because "Facebook" has no inherent hue; "Negative" does, and any
 * chart splitting by sentiment has to use the same three or the reader has to
 * re-learn the legend on every card.
 *
 * Three entries and not four: `Mixed` was removed from the sentiment fixture in
 * 2026-09, which is what makes a three-tone triple sufficient. See the note
 * there.
 */
const SENTIMENT_TOKENS: Readonly<Record<string, ChartSeriesToken>> = {
  positive: 'chart-4',
  neutral: 'chart-5',
  negative: 'chart-6',
};

/**
 * The three sentiments in the order a tie breaks, best first.
 *
 * Fixture order, which is also the order the chips list them, so a tie on a
 * word cloud resolves the same way a tie resolves anywhere else in the app.
 */
const SENTIMENT_ORDER = ['positive', 'neutral', 'negative'] as const;

export type Sentiment = (typeof SENTIMENT_ORDER)[number];

/**
 * The sentiment that **most** of a group of mentions carries, and the token
 * that paints it.
 *
 * Plurality, not majority: whichever of the three is largest wins, even at 40 /
 * 35 / 25. Settled with the user (2026-09-17) against the two alternatives —
 * "needs >50% or it falls back to neutral", which makes `chart/5` mean both
 * "genuinely neutral" and "too mixed to call" and so stops being readable as a
 * tone at all; and a net-score band, which drops neutral mentions out of the
 * comparison entirely.
 *
 * A tie goes to the earlier entry in `SENTIMENT_ORDER`. Something has to break
 * it, and picking the fixture's own order means the same tie resolves the same
 * way here, in a table's badge and in a legend — the "similar-but-different
 * predicate" trap this file already warns about, in its colour-picking hat.
 *
 * `null` when there is nothing to read. A group with no mentions has no
 * dominant tone, and returning `positive` for it would paint an empty word
 * green.
 */
export function dominantSentiment(
  counts: Readonly<Record<string, number>>,
): { sentiment: Sentiment; token: ChartSeriesToken; count: number; share: number } | null {
  let best: Sentiment | null = null;
  let bestCount = 0;
  let total = 0;

  for (const sentiment of SENTIMENT_ORDER) {
    const count = counts[sentiment] ?? 0;
    total += count;
    /* Strictly greater, so the first of a tie wins — see the note above. */
    if (count > bestCount) {
      best = sentiment;
      bestCount = count;
    }
  }

  if (best === null || total === 0) return null;

  return {
    sentiment: best,
    token: SENTIMENT_TOKENS[best],
    count: bestCount,
    share: (bestCount / total) * 100,
  };
}

/**
 * The colour for one value of a dimension.
 *
 * Indexed on fixture order, which is the fix for a bug that reads as a
 * rendering glitch rather than a data one: colour by position in the filtered
 * result and deselecting Grameenphone promotes Banglalink into `chart-1`,
 * recolouring every chart on the page mid-session. Indexing the readonly tuple
 * also means the result is a `ChartSeriesToken`, so no hex can be written here.
 *
 * Sentiment is the one exception, and for the same underlying reason: its colour
 * must not move, and unlike the others it has a meaning to be faithful to.
 */
export function seriesToken(key: DimensionKey, value: string): ChartSeriesToken {
  if (key === 'sentiment') {
    const byMeaning = SENTIMENT_TOKENS[value];
    if (byMeaning) return byMeaning;
  }
  const index = dimensionIndex[key][value] ?? 0;
  return chartSeriesTokens[index % chartPaletteSize];
}

export function aggregate(rows: readonly MentionRow[], spec: AggregateSpec): AggregateResult {
  const bucket = spec.bucket ?? autoBucket(spec.from, spec.to);
  const metric = metrics[spec.metric];

  /*
   * The axis comes from the *range*, not from the rows: every bucket in range is
   * emitted, including the ones the filter emptied. See `bucketsIn`.
   */
  const starts = bucketsIn(spec.from, spec.to, bucket);

  /*
   * Rows are grouped, then each group is reduced **as a whole array**. Grouping
   * first is what lets `MetricDef.reduce` stay non-additive — a running
   * accumulator would turn `nps` into a mean of means.
   */
  const groups = new Map<number, Map<string, MentionRow[]>>();
  for (const start of starts) groups.set(start, new Map());

  const SINGLE = spec.metric;
  for (const row of rows) {
    const start = bucketStart(row.day, bucket);
    const bySeries = groups.get(start);
    /* A row outside the requested range is skipped rather than inventing a bucket. */
    if (!bySeries) continue;
    const seriesKey = spec.splitBy ? row[spec.splitBy] : SINGLE;
    const list = bySeries.get(seriesKey);
    if (list) list.push(row);
    else bySeries.set(seriesKey, [row]);
  }

  const series = spec.splitBy
    ? seriesForSplit(rows, spec.splitBy)
    : [{ key: SINGLE, label: metric.label, color: chartSeriesTokens[0] }];

  const chartRows: ChartRow[] = starts.map((start) => {
    const bySeries = groups.get(start)!;
    const row: ChartRow = { [X_KEY]: bucketLabel(start, bucket) };
    for (const s of series) {
      /*
       * `0`, never `null`. A filter that empties a Tuesday has to draw a zero:
       * `null` makes the line jump the gap and read as continuous, hiding
       * exactly what the user filtered for. (`isEmpty` treats all-null as an
       * empty chart, which is a different state and stays reachable.)
       */
      const group = bySeries.get(s.key);
      row[s.key] = group ? metric.reduce(group) : 0;
    }
    return row;
  });

  return {
    rows: chartRows,
    xKey: X_KEY,
    series,
    /* The same array the buckets were built from. This is the reconciliation. */
    total: metric.reduce(rows),
    bucket,
    rowsAt: (index) => {
      const start = starts[index];
      if (start === undefined) return [];
      return [...(groups.get(start)?.values() ?? [])].flat();
    },
  };
}

/**
 * One series per value **present in the rows**, ordered and coloured by the
 * chips' order.
 *
 * Present-in-the-rows rather than every possible value, so deselecting a brand
 * removes its line instead of leaving a flat zero in the legend. Order and
 * colour still come from the fixtures, so the survivors keep the colours they
 * had.
 */
function seriesForSplit(rows: readonly MentionRow[], splitBy: DimensionKey): ChartSeries[] {
  const present = new Set<string>();
  for (const row of rows) present.add(row[splitBy]);

  return [...present]
    .sort((a, b) => (dimensionIndex[splitBy][a] ?? 0) - (dimensionIndex[splitBy][b] ?? 0))
    .map((value) => ({
      key: value,
      label: labelOf(splitBy, value),
      color: seriesToken(splitBy, value),
    }));
}
